import { useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { TrendingUp, DollarSign, Calendar, Users, ArrowUpRight } from "lucide-react";

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const revenueData = [
  { month: "Oct", revenue: 18200, bookings: 142 },
  { month: "Nov", revenue: 22400, bookings: 178 },
  { month: "Dec", revenue: 28900, bookings: 215 },
  { month: "Jan", revenue: 31200, bookings: 234 },
  { month: "Feb", revenue: 38600, bookings: 289 },
  { month: "Mar", revenue: 48290, bookings: 342 },
];

const bookingsByCategory = [
  { category: "Hair & Beauty", bookings: 420, revenue: 16800 },
  { category: "Wellness", bookings: 310, revenue: 14200 },
  { category: "Tattoo", bookings: 180, revenue: 12600 },
  { category: "Nails", bookings: 210, revenue: 6300 },
  { category: "Barber", bookings: 280, revenue: 8400 },
  { category: "Makeup", bookings: 140, revenue: 5600 },
];

const categoryDistribution = [
  { name: "Hair & Beauty", value: 420 },
  { name: "Wellness", value: 310 },
  { name: "Tattoo", value: 180 },
  { name: "Nails", value: 210 },
  { name: "Barber", value: 280 },
  { name: "Makeup", value: 140 },
];

const PIE_COLORS = [
  "hsl(357, 25%, 63%)",
  "hsl(60, 13%, 57%)",
  "hsl(51, 44%, 71%)",
  "hsl(17, 47%, 70%)",
  "hsl(44, 45%, 55%)",
  "hsl(30, 10%, 50%)",
];

const topProviders = [
  { name: "Amara Beauty Studio", bookings: 124, revenue: "$12,400", growth: "+18%" },
  { name: "Zen Wellness Spa", bookings: 89, revenue: "$9,200", growth: "+24%" },
  { name: "Urban Barber Co.", bookings: 98, revenue: "$8,600", growth: "+12%" },
  { name: "Ink & Soul Tattoos", bookings: 67, revenue: "$7,800", growth: "+31%" },
  { name: "Glow Nails Lounge", bookings: 45, revenue: "$4,100", growth: "-5%" },
];

const weeklyBookings = [
  { day: "Mon", thisWeek: 42, lastWeek: 35 },
  { day: "Tue", thisWeek: 38, lastWeek: 40 },
  { day: "Wed", thisWeek: 55, lastWeek: 45 },
  { day: "Thu", thisWeek: 48, lastWeek: 42 },
  { day: "Fri", thisWeek: 62, lastWeek: 52 },
  { day: "Sat", thisWeek: 78, lastWeek: 68 },
  { day: "Sun", thisWeek: 32, lastWeek: 28 },
];

const summaryCards = [
  { label: "Avg. Booking Value", value: "$62.40", change: "+8.3%", icon: DollarSign },
  { label: "Completion Rate", value: "94.2%", change: "+2.1%", icon: Calendar },
  { label: "New Users (Month)", value: "348", change: "+15.6%", icon: Users },
  { label: "Repeat Customers", value: "67%", change: "+4.8%", icon: TrendingUp },
];

const AdminAnalytics = () => {
  const [period, setPeriod] = useState<"6m" | "3m" | "1m">("6m");

  const chartData = period === "1m" ? revenueData.slice(-1) : period === "3m" ? revenueData.slice(-3) : revenueData;

  return (
    <motion.div key="analytics" {...fadeIn}>
      {/* Period toggle + summary */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Analytics</h3>
        <div className="flex rounded-lg border border-border overflow-hidden">
          {(["6m", "3m", "1m"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                period === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {summaryCards.map(({ label, value, change, icon: Icon }) => (
          <div key={label} className="rounded-xl border border-border bg-background p-3.5">
            <div className="flex items-center justify-between mb-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <span className="flex items-center gap-0.5 text-[11px] font-medium text-emerald-600">
                <ArrowUpRight className="h-3 w-3" />
                {change}
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Revenue trend */}
      <div className="rounded-xl border border-border bg-background p-4 mb-4">
        <h4 className="font-semibold text-foreground mb-4">Revenue Trend</h4>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(357, 25%, 63%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(357, 25%, 63%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(44, 30%, 85%)" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(0, 0%, 100%)",
                  border: "1px solid hsl(44, 30%, 85%)",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
              />
              <Area type="monotone" dataKey="revenue" stroke="hsl(357, 25%, 63%)" fill="url(#revenueGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Weekly bookings + Category distribution */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="rounded-xl border border-border bg-background p-4">
          <h4 className="font-semibold text-foreground mb-4">Weekly Bookings Comparison</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyBookings} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(44, 30%, 85%)" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(60, 13%, 40%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(60, 13%, 40%)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(0, 0%, 100%)",
                    border: "1px solid hsl(44, 30%, 85%)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="lastWeek" name="Last Week" fill="hsl(44, 45%, 85%)" radius={[4, 4, 0, 0]} barSize={14} />
                <Bar dataKey="thisWeek" name="This Week" fill="hsl(357, 25%, 63%)" radius={[4, 4, 0, 0]} barSize={14} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-background p-4">
          <h4 className="font-semibold text-foreground mb-4">Bookings by Category</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  dataKey="value"
                  stroke="none"
                >
                  {categoryDistribution.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(0, 0%, 100%)",
                    border: "1px solid hsl(44, 30%, 85%)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Revenue by category table */}
      <div className="rounded-xl border border-border bg-background p-4 mb-4">
        <h4 className="font-semibold text-foreground mb-3">Revenue by Category</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2.5 font-medium text-muted-foreground">Category</th>
                <th className="text-right py-2.5 font-medium text-muted-foreground">Bookings</th>
                <th className="text-right py-2.5 font-medium text-muted-foreground">Revenue</th>
                <th className="text-right py-2.5 font-medium text-muted-foreground hidden sm:table-cell">Avg. Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {bookingsByCategory.map((c) => (
                <tr key={c.category} className="hover:bg-muted/20 transition-colors">
                  <td className="py-2.5 font-medium text-foreground">{c.category}</td>
                  <td className="py-2.5 text-right text-muted-foreground">{c.bookings}</td>
                  <td className="py-2.5 text-right font-medium text-foreground">${c.revenue.toLocaleString()}</td>
                  <td className="py-2.5 text-right text-muted-foreground hidden sm:table-cell">${(c.revenue / c.bookings).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top performers */}
      <div className="rounded-xl border border-border bg-background p-4">
        <h4 className="font-semibold text-foreground mb-3">Top Performing Providers</h4>
        <div className="space-y-3">
          {topProviders.map((p, i) => (
            <div key={p.name} className="flex items-center gap-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                <p className="text-[11px] text-muted-foreground">{p.bookings} bookings</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{p.revenue}</p>
                <p className={`text-[11px] font-medium ${p.growth.startsWith("+") ? "text-emerald-600" : "text-destructive"}`}>{p.growth}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default AdminAnalytics;
